import type {
  WorksheetAssignSchedule,
  WorksheetAssignScheduleCount,
  WorksheetAssignScheduleStatus,
} from '@mathflat/domain/Worksheet'
import { makeAutoObservable, runInAction } from 'mobx'

import { worksheetAssignScheduleController } from '@/controller/worksheetAssignSchedule.controller'

import { confirmCancelSchedule } from '../_utils/scheduleConfirm'
import { toastScheduleError } from '../_utils/scheduleError'

type Status = WorksheetAssignScheduleStatus
type Detail = WorksheetAssignSchedule

export const LIST_PAGE_SIZE = 20

/** 출제 예약 목록 뷰의 탭·카드·건수. 목록 뷰가 열릴 때 만들고 닫히면 버린다 */
export class ScheduleListService {
  status: Status = 'WAITING'
  items: Detail[] = []
  counts: WorksheetAssignScheduleCount | null = null
  loadState: 'loading' | 'error' | 'ready' = 'loading'
  isLoadingMore = false
  private page = 0
  private hasNext = true
  /** 탭 전환 중 늦게 도착한 이전 요청의 응답을 버리기 위한 세대 번호 */
  private requestId = 0

  constructor() {
    makeAutoObservable<ScheduleListService, 'page' | 'hasNext' | 'requestId'>(this, {
      page: false,
      hasNext: false,
      requestId: false,
    })
  }

  /** 첫 페이지와 건수를 함께 읽는다. 탭이 바뀌거나 취소 뒤에도 이걸로 처음부터 다시 그린다 */
  load = async () => {
    const requestId = ++this.requestId
    // 이전 탭의 다음 페이지 요청이 진행 중이어도 새 목록은 깨끗한 상태로 시작한다
    this.isLoadingMore = false
    this.loadState = 'loading'
    this.page = 0
    this.hasNext = true
    try {
      const [slice, counts] = await Promise.all([
        worksheetAssignScheduleController.getTargets({
          status: this.status,
          page: 0,
          size: LIST_PAGE_SIZE,
        }),
        worksheetAssignScheduleController.getCounts(),
      ])
      runInAction(() => {
        if (requestId !== this.requestId) return
        this.items = slice.content
        this.hasNext = !slice.last
        this.counts = counts
        this.loadState = 'ready'
      })
    } catch {
      runInAction(() => {
        if (requestId !== this.requestId) return
        this.loadState = 'error'
      })
    }
  }

  setStatus = (status: Status) => {
    this.status = status
    void this.load()
  }

  /** 스크롤 끝에서 다음 페이지를 덧붙인다 */
  loadMore = async () => {
    if (!this.hasNext || this.isLoadingMore || this.loadState !== 'ready') return
    this.isLoadingMore = true
    const requestId = this.requestId
    try {
      const slice = await worksheetAssignScheduleController.getTargets({
        status: this.status,
        page: this.page + 1,
        size: LIST_PAGE_SIZE,
      })
      runInAction(() => {
        if (requestId !== this.requestId) return
        this.items = [...this.items, ...slice.content]
        this.page += 1
        this.hasNext = !slice.last
      })
    } catch (error) {
      if (requestId === this.requestId) toastScheduleError(error)
    } finally {
      runInAction(() => {
        if (requestId === this.requestId) this.isLoadingMore = false
      })
    }
  }

  /** 대기 탭의 예약 취소. 얼럿 → DELETE → 목록을 처음부터 다시 읽는다 */
  cancel = async (batchId: string) => {
    if (!(await confirmCancelSchedule())) return
    try {
      await worksheetAssignScheduleController.cancel(batchId)
    } catch (error) {
      // 이미 출제됐거나 사라진 예약이면 다시 읽어 최신 상태로 되돌린다
      toastScheduleError(error)
    }
    await this.load()
  }
}
